import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { sessionService as ss } from 'redux-react-session';
import _includes from 'lodash/includes';
import { trackError } from 'services/sentry';
// Local files
import { clearStore as clearStoreAction } from 'actions/sessions';
import {
  setError as setErrorAction,
  hideError as hideErrorAction
} from 'actions/error';
import { errorTypes } from 'helpers/constants';
import { isIndexedDBError } from 'helpers';

const { ACCESS_TOKEN_INVALID, PARAMETER_MISSING, AUTHORIZE_DATA_INVALID } = errorTypes;

const useError = () => {
  const dispatch = useDispatch();
  const clearStore = useCallback(() => dispatch(clearStoreAction()), [dispatch]);
  const showError = useCallback(error => dispatch(setErrorAction(error)), [dispatch]);
  const hideError = useCallback(() => dispatch(hideErrorAction()), [dispatch]);

  const deleteSession = useCallback(() =>
    ss.loadSession()
      .then(() => clearStore().then(() => ss.deleteSession().then(() => ss.deleteUser())))
      .catch((error) => {
        // Don't throw IndexedDB errors - these are browser-level issues
        if (isIndexedDBError(error)) {
          // Try to clear store and session anyway, but don't throw
          return clearStore().then(() => ss.deleteSession().then(() => ss.deleteUser()).catch(() => {})).catch(() => {});
        }
        // Handle both string errors and Error objects
        const errorMessage = typeof error === 'string' ? error : (error?.message || '');
        if (errorMessage.toLowerCase().includes('session not found')) {
          return clearStore().then(() => ss.deleteSession().then(() => ss.deleteUser()).catch(() => {})).catch(() => {});
        }
        throw error;
      })
  , [clearStore]);

  const setError = useCallback(error => new Promise(resolve => {
    const type = error?.response?.data?.error?.type;
    const isWrongToken = type === ACCESS_TOKEN_INVALID || (type === PARAMETER_MISSING && _includes(error?.response?.data?.error?.message, 'access_token'));

    // Skip Sentry tracking for user-facing errors that are expected:
    // - authorize_data_invalid: invalid email/password combinations (user input validation)
    // - geolocation permission denial
    // - errors explicitly marked to skip Sentry
    if (!error?.skipSentry && type !== AUTHORIZE_DATA_INVALID) {
      // Capture error in Sentry with additional context
      trackError(error, {
        type,
        isWrongToken,
        response: error?.response?.data
      });
    }

    if (isWrongToken) {
      // Catch rejections from deleteSession to prevent unhandled promise rejections
      // "Session not found" is expected when there's no session to delete
      deleteSession()
        .then(() => resolve())
        .catch((deleteError) => {
          // Handle both string errors and Error objects
          const errorMessage = typeof deleteError === 'string' ? deleteError : (deleteError?.message || '');
          // "Session not found" is expected - session may already be deleted
          if (errorMessage.toLowerCase().includes('session not found')) {
            resolve(); // Resolve successfully since session cleanup is already done
          } else {
            // For other errors, still resolve to prevent unhandled rejection
            // but log the error
            console.warn('Error during session deletion:', deleteError);
            resolve();
          }
        });
    } else {
      resolve(showError(error));
    }
  }), [deleteSession, showError]);

  return { setError, hideError };
};

export default useError;